// frontend/src/widgets/OpportunityDetailWidget.jsx
// Project:      Vision ANPR by Castle Traffic Ltd - Automatic Number Plate Recognition
// Author:       Leon du Plooy - Castle Traffic Ltd - Copyright 2026
// Contact:      dev@castle-traffic.co.uk
// Created:      2026-01-14
// Updated:      2026-09-08 16:37 BST
//
// Description:
//   Opportunity detail widget (middle column).
//   Displays opportunity fields and allows edits when canEdit=true.
//   Uses shared Axios instance (api) ONLY.
//
// Updates (2026-01-16):
//   • “Saturated” editable fields (stage, status, raised_by_contact_id, expected_close_date, amount, probability, notes, name).
//   • Adds read-only audit fields (id, created_at, updated_at, won_at, lost_at, owner_user_id).
//   • Adds dirty-check (Save only enabled when changes exist).
//   • Adds basic validation (probability 0–100, amount numeric >= 0).
//   • Keeps Primary Contact selector filtered by customer_id and sorted A→Z.
//   • Keeps robust owner resource name resolution fallback.
//   NOTE: This file intentionally avoids JS nullish coalescing (??) to prevent eslint "?? and ||" mixing errors.
//
// Patch (2026-01-21):
//   • Align Stage values with DB enum public.opportunity_stage:
//       qualification, needs_analysis, proposal, negotiation, verbal_commit, closed
//   • Display friendly labels (Discovery/Verbal Commit) while storing/sending canonical enum values.
//   • Tolerate legacy "discovery" by mapping to "needs_analysis" on display + save.
//
// Mobile Responsiveness (2026-09-08):
//   • Preserves one render path for desktop and mobile.
//   • Converts all two-column field groups to one column below 768px while
//     retaining two columns at md and above.
//   • Makes Access / Save controls full-width and touch-friendly on mobile.
//   • Uses natural widget height and visible overflow on mobile so lower sections
//     are not clipped by desktop-oriented widget heights.
//   • Retains desktop fill/overflow behaviour at md and above.
//   • Allows validation, ownership, audit and access-state text to wrap safely.
//   • Clamps portal tooltip width to the available viewport on narrow screens.
//
// Operational Notes:
//   • Existing opportunity load/save, access, owner and contact behaviour is preserved.
//   • Existing API routes and fallback sequence are unchanged.
//   • No additional API calls, polling or duplicate render trees are introduced.
//   • Tenant ID remains read-only display data from the opportunity payload and is
//     never submitted by this widget.

import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import ReactDOM from "react-dom";

import api from "../api/axiosInstance";
import { useTheme } from "../context/ThemeContext.jsx";

const toInt = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const isNil = (v) => v === null || v === undefined;

const getContactCustomerId = (c) => {
  if (!c) return null;
  return toInt(!isNil(c.customer_id) ? c.customer_id : c.customerId);
};

const getContactName = (c) => {
  if (!c) return "";
  const full = c.full_name || c.fullName;
  if (full && String(full).trim()) return String(full).trim();
  const first = c.first_name || c.firstName || "";
  const last = c.last_name || c.lastName || "";
  return `${first} ${last}`.trim();
};

const normDateForInput = (v) => {
  if (!v) return "";
  const s = String(v);
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return "";
  const yyyy = d.getUTCFullYear();
  const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(d.getUTCDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const fmtIsoLike = (v) => {
  if (!v) return "";
  const d = new Date(String(v));
  if (Number.isNaN(d.getTime())) return String(v);
  return d.toLocaleString();
};

const toNumberOrNull = (v) => {
  if (isNil(v)) return null;
  const s = String(v).trim();
  if (!s) return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
};

const resourceDisplayName = (r) => {
  if (!r) return "";
  const nm = r.name || r.display_name || r.displayName;
  if (nm && String(nm).trim()) return String(nm).trim();
  const first = r.first_name || r.firstName || "";
  const last = r.last_name || r.lastName || "";
  const joined = `${first} ${last}`.trim();
  return joined || "";
};

const shallowPickComparable = (o) => {
  if (!o) return {};
  return {
    name: o.name || "",
    stage: o.stage || "",
    status: o.status || "",
    notes: o.notes || "",
    expected_close_date: o.expected_close_date || null,
    primary_contact_id: isNil(o.primary_contact_id) ? null : toInt(o.primary_contact_id),
    raised_by_contact_id: isNil(o.raised_by_contact_id) ? null : toInt(o.raised_by_contact_id),
    probability: isNil(o.probability) ? null : toNumberOrNull(o.probability),
    amount: isNil(o.amount) ? null : toNumberOrNull(o.amount),
    owner_resource_id: isNil(o.owner_resource_id) ? null : toInt(o.owner_resource_id),
  };
};

const stableStringify = (obj) => {
  const keys = Object.keys(obj || {}).sort();
  const out = {};
  keys.forEach((k) => {
    out[k] = obj[k];
  });
  return JSON.stringify(out);
};

// ------------------------
// Stage enum alignment helpers
// ------------------------
const norm = (v) => String(v || "").trim().toLowerCase();

const STAGE_LABELS = {
  qualification: "Qualification",
  needs_analysis: "Discovery",
  proposal: "Proposal",
  negotiation: "Negotiation",
  verbal_commit: "Verbal Commit",
  closed: "Closed",
};

const STAGE_ALIASES = {
  discovery: "needs_analysis",
  analysis: "needs_analysis",
  needsanalysis: "needs_analysis",
  verbalcommit: "verbal_commit",
};

const canonicalStage = (s) => {
  const k = norm(s).replace(/\s+/g, "_");
  return STAGE_ALIASES[k] || k;
};

const stageLabel = (s) => {
  const k = canonicalStage(s);
  return STAGE_LABELS[k] || String(s || "");
};

// ------------------------
// Gold-standard tooltip:
// - rendered via React portal to document.body (prevents clipping/stacking issues)
// - no question-mark cursor
// - theme primary background, white text, multiline (pre-line)
// - follows cursor + clamps to viewport
// - hides on mouse leave / scroll / ESC / blur
// ------------------------
let __tipSeq = 1;

function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

function PortalTooltip({ open, text, x, y, theme }) {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  if (!mounted || !open || !text) return null;

  const bg = (theme && theme.primary_color) || "#111827";
  const pad = 10;

  const vw = typeof window !== "undefined" ? window.innerWidth : 1200;
  const vh = typeof window !== "undefined" ? window.innerHeight : 800;

  const maxWidth = Math.max(220, Math.min(360, vw - pad * 2));

  // rough sizing for clamp; browser will handle true size
  const approxWidth = clamp(
    Math.max(220, String(text).length * 7),
    220,
    maxWidth
  );
  const approxHeight = 52 + (String(text).split("\n").length - 1) * 16;

  const maxLeft = Math.max(pad, vw - approxWidth - pad);
  const maxTop = Math.max(pad, vh - approxHeight - pad);

  const left = clamp((x || 0) + 14, pad, maxLeft);
  const top = clamp((y || 0) + 14, pad, maxTop);

  const node = (
    <div
      role="tooltip"
      style={{
        position: "fixed",
        left,
        top,
        zIndex: 2147483647,
        pointerEvents: "none",
        background: bg,
        color: "#fff",
        borderRadius: 10,
        padding: "10px 12px",
        fontSize: 12,
        fontWeight: 700,
        width: "max-content",
        maxWidth,
        whiteSpace: "pre-line",
        overflowWrap: "anywhere",
        boxShadow: "0 10px 25px rgba(0,0,0,0.25)",
      }}
    >
      {String(text)}
    </div>
  );

  return ReactDOM.createPortal(node, document.body);
}

function useGoldTooltip(theme) {
  const [state, setState] = useState({
    open: false,
    text: "",
    x: 0,
    y: 0,
    id: 0,
  });

  const hide = useCallback(() => {
    setState((s) => (s.open ? { ...s, open: false } : s));
  }, []);

  const readXY = useCallback((evt) => {
    const e = evt && evt.nativeEvent ? evt.nativeEvent : evt;
    const touch =
      e && e.touches && e.touches.length
        ? e.touches[0]
        : e && e.changedTouches && e.changedTouches.length
          ? e.changedTouches[0]
          : null;

    const x = touch
      ? touch.clientX
      : e && typeof e.clientX === "number"
        ? e.clientX
        : 0;

    const y = touch
      ? touch.clientY
      : e && typeof e.clientY === "number"
        ? e.clientY
        : 0;

    return { x, y };
  }, []);

  const show = useCallback(
    (text, evt) => {
      if (!text) return;
      const { x, y } = readXY(evt);
      setState({
        open: true,
        text: String(text),
        x,
        y,
        id: __tipSeq++,
      });
    },
    [readXY]
  );

  const move = useCallback(
    (evt) => {
      const { x, y } = readXY(evt);
      setState((s) => (s.open ? { ...s, x, y } : s));
    },
    [readXY]
  );

  useEffect(() => {
    const onScroll = () => hide();
    const onBlur = () => hide();
    const onKey = (e) => {
      if (e && e.key === "Escape") hide();
    };

    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("blur", onBlur);
    window.addEventListener("keydown", onKey);

    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("blur", onBlur);
      window.removeEventListener("keydown", onKey);
    };
  }, [hide]);

  const Tooltip = useCallback(
    () => (
      <PortalTooltip
        open={state.open}
        text={state.text}
        x={state.x}
        y={state.y}
        theme={theme}
      />
    ),
    [state.open, state.text, state.x, state.y, theme]
  );

  const bind = useCallback(
    (text) => ({
      onMouseEnter: (e) => show(text, e),
      onMouseMove: (e) => move(e),
      onMouseLeave: () => hide(),
      onTouchStart: (e) => show(text, e),
      onTouchEnd: () => {
        window.setTimeout(() => hide(), 1400);
      },
      // kill native tooltip paths
      title: undefined,
    }),
    [show, move, hide]
  );

  return { Tooltip, bind, hide };
}

function LabelWithTip({ label, tip, bind, style }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <span
        {...(tip ? bind(tip) : {})}
        style={{
          cursor: "default", // IMPORTANT: no question-mark cursor
          display: "inline-block",
          userSelect: "none",
          ...style,
        }}
      >
        {label}
      </span>
    </div>
  );
}

export default function OpportunityDetailWidget({
  opportunity = null,
  loading = false,
  error = "",
  canEdit = false,
  accessLoading = false,
  accessError = "",
  actorResourceId = null,
  accessActiveRows = [],
  onSave,
  onOpenAccess,
  onReload,
}) {
  const { theme } = useTheme();
  const { Tooltip, bind } = useGoldTooltip(theme);

  // ✅ DB enum order
  const STAGE_OPTIONS = useMemo(
    () => ["qualification", "needs_analysis", "proposal", "negotiation", "verbal_commit", "closed"],
    []
  );
  const STATUS_OPTIONS = useMemo(() => ["open", "won", "lost", "on_hold"], []);

  const [draft, setDraft] = useState(null);
  const [baseSnapshot, setBaseSnapshot] = useState("{}");

  const [contacts, setContacts] = useState([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [contactsErr, setContactsErr] = useState("");

  const [customerName, setCustomerName] = useState("");

  const [salesOwners, setSalesOwners] = useState([]);
  const [salesOwnersLoading, setSalesOwnersLoading] = useState(false);
  const [salesOwnersErr, setSalesOwnersErr] = useState("");

  const [saveErr, setSaveErr] = useState("");
  const [validationErr, setValidationErr] = useState("");

  // Derived values/hooks MUST be before any early returns (eslint hooks rule)
  const customerId = useMemo(() => {
    if (!draft) return null;
    const raw = !isNil(draft.customer_id) ? draft.customer_id : draft.client_id;
    return toInt(raw);
  }, [draft]);

  const ownerResourceId = useMemo(
    () => (draft ? toInt(draft.owner_resource_id) : null),
    [draft]
  );

  const ownerUserId = useMemo(
    () => (draft ? toInt(draft.owner_user_id) : null),
    [draft]
  );

  const isDirty = useMemo(() => {
    if (!draft) return false;
    return stableStringify(shallowPickComparable(draft)) !== baseSnapshot;
  }, [draft, baseSnapshot]);

  const wrap = useMemo(
    () => ({
      background: theme?.card_bg || "#fff",
      borderRadius: 14,
      padding: 14,
      boxShadow: theme?.card_shadow || "0 1px 2px rgba(0,0,0,0.06)",
      border: theme?.card_border || "1px solid rgba(0,0,0,0.06)",
      display: "flex",
      flexDirection: "column",
      minHeight: 0,
      boxSizing: "border-box",
    }),
    [theme]
  );

  const labelStyle = useMemo(
    () => ({
      fontSize: 12,
      opacity: 0.75,
      fontWeight: 800,
    }),
    []
  );

  const hint = useMemo(
    () => ({
      marginTop: 6,
      fontSize: 11,
      opacity: 0.65,
      overflowWrap: "anywhere",
    }),
    []
  );

  const input = useMemo(
    () => ({
      width: "100%",
      minWidth: 0,
      borderRadius: 10,
      border: "1px solid rgba(0,0,0,0.12)",
      padding: "10px 12px",
      fontSize: 12,
      outline: "none",
      background: "#fff",
      boxSizing: "border-box",
    }),
    []
  );

  const sectionTitle = useMemo(
    () => ({
      fontSize: 12,
      fontWeight: 950,
      opacity: 0.9,
      marginTop: 14,
      paddingTop: 12,
      borderTop: "1px solid rgba(0,0,0,0.06)",
    }),
    []
  );

  // ✅ always operate on canonical stage string
  const stageValue = String(canonicalStage((draft && draft.stage) || ""));
  const statusValue = String((draft && draft.status) || "");

  const stageOptions = useMemo(() => {
    const base = STAGE_OPTIONS.slice(0);
    const v = String(stageValue || "").trim();
    if (v && base.indexOf(v) === -1) base.unshift(v);
    return base;
  }, [STAGE_OPTIONS, stageValue]);

  const statusOptions = useMemo(() => {
    const base = STATUS_OPTIONS.slice(0);
    const v = String(statusValue || "").trim();
    if (v && base.indexOf(v) === -1) base.unshift(v);
    return base;
  }, [STATUS_OPTIONS, statusValue]);

  useEffect(() => {
    const next = opportunity ? { ...opportunity } : null;

    // sanitize legacy stage immediately so UI + save are consistent
    if (next && next.stage) next.stage = canonicalStage(next.stage);

    setDraft(next);
    setSaveErr("");
    setValidationErr("");
    setBaseSnapshot(stableStringify(shallowPickComparable(next)));
  }, [opportunity]);

  // Customer resolve (unchanged)
  useEffect(() => {
    let mounted = true;

    const run = async () => {
      setCustomerName("");
      if (!customerId) return;

      try {
        try {
          const { data } = await api.get(`/api/customers/${customerId}`);
          if (!mounted) return;
          setCustomerName(
            String(data?.name || data?.company_name || data?.companyName || "") || ""
          );
          return;
        } catch {
          const { data } = await api.get(`/api/clients/${customerId}`);
          if (!mounted) return;
          setCustomerName(
            String(data?.name || data?.company_name || data?.companyName || "") || ""
          );
        }
      } catch {
        if (!mounted) return;
        setCustomerName("");
      }
    };

    run();

    return () => {
      mounted = false;
    };
  }, [customerId]);

  // Sales owners load (active + is_sales=true)
  const loadSalesOwners = useCallback(async () => {
    setSalesOwners([]);
    setSalesOwnersErr("");
    setSalesOwnersLoading(true);

    try {
      const { data } = await api.get(`/api/resources?include_inactive=true&limit=500`);
      const arr = Array.isArray(data)
        ? data
        : Array.isArray(data?.items)
          ? data.items
          : [];

      const filtered = arr
        .filter((r) => {
          const active = r && r.active === true;
          const isSales = r && r.is_sales === true;
          return active && isSales;
        })
        .map((r) => ({
          id: toInt(r?.id),
          label: resourceDisplayName(r) || `Resource #${toInt(r?.id) || ""}`,
        }))
        .filter((x) => x.id);

      filtered.sort((a, b) => {
        const an = (a.label || "").toLowerCase();
        const bn = (b.label || "").toLowerCase();
        if (an < bn) return -1;
        if (an > bn) return 1;
        return (a.id || 0) - (b.id || 0);
      });

      setSalesOwners(filtered);
    } catch (e) {
      setSalesOwnersErr(
        e?.response?.data?.detail ||
          String(e?.message || "Failed to load sales owners")
      );
      setSalesOwners([]);
    } finally {
      setSalesOwnersLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSalesOwners();
  }, [loadSalesOwners]);

  // contacts load (filtered + sorted)
  const loadContactsForCustomer = useCallback(async (cid) => {
    setContacts([]);
    setContactsErr("");

    if (!cid) return;

    setContactsLoading(true);

    try {
      const candidates = [
        `/api/contacts?customer_id=${cid}&limit=500`,
        `/api/contacts?customerId=${cid}&limit=500`,
        `/api/clients/${cid}/contacts?limit=500`,
        `/api/contacts?limit=500`,
      ];

      let data = null;
      let lastErr = null;

      for (const url of candidates) {
        try {
          const res = await api.get(url);
          data = res.data;
          lastErr = null;
          break;
        } catch (e) {
          lastErr = e;
        }
      }

      if (lastErr && data == null) throw lastErr;

      const arr = Array.isArray(data)
        ? data
        : Array.isArray(data?.items)
          ? data.items
          : [];

      const filtered = arr.filter((c) => getContactCustomerId(c) === cid);

      filtered.sort((a, b) => {
        const an = (getContactName(a) || "").toLowerCase();
        const bn = (getContactName(b) || "").toLowerCase();
        if (an < bn) return -1;
        if (an > bn) return 1;
        return (toInt(a?.id) || 0) - (toInt(b?.id) || 0);
      });

      setContacts(filtered);
    } catch (e) {
      setContactsErr(
        e?.response?.data?.detail ||
          String(e?.message || "Failed to load contacts")
      );
      setContacts([]);
    } finally {
      setContactsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadContactsForCustomer(customerId);
  }, [customerId, loadContactsForCustomer]);

  const contactOptions = useMemo(() => {
    return (contacts || []).map((c) => {
      const id = toInt(c?.id);
      const name = getContactName(c) || `Contact ${id || ""}`;
      const email = c?.email ? ` · ${c.email}` : "";
      const primary = c?.is_primary ? " ★" : "";
      return {
        id,
        label: `${name}${email}${primary}`,
      };
    });
  }, [contacts]);

  const buildPatch = useCallback((d) => {
    if (!d) return {};

    const st = canonicalStage(d.stage);

    return {
      name: d.name || "",
      stage: st || "",
      status: d.status || "",
      notes: d.notes || "",
      expected_close_date: d.expected_close_date || null,
      primary_contact_id: isNil(d.primary_contact_id)
        ? null
        : toInt(d.primary_contact_id),
      raised_by_contact_id: isNil(d.raised_by_contact_id)
        ? null
        : toInt(d.raised_by_contact_id),
      probability: toNumberOrNull(d.probability),
      amount: toNumberOrNull(d.amount),
      owner_resource_id: isNil(d.owner_resource_id)
        ? null
        : toInt(d.owner_resource_id),
    };
  }, []);

  const validateDraft = useCallback(
    (d) => {
      if (!d) return "";

      // stage must be canonical DB enum value
      const st = canonicalStage(d.stage);
      if (st && (STAGE_OPTIONS || []).indexOf(st) === -1) {
        return `Stage is invalid (${String(d.stage)}).`;
      }

      const prob = toNumberOrNull(d.probability);

      if (
        !isNil(d.probability) &&
        String(d.probability).trim() !== "" &&
        prob === null
      ) {
        return "Probability must be a number.";
      }

      if (prob !== null && (prob < 0 || prob > 100)) {
        return "Probability must be between 0 and 100.";
      }

      const amt = toNumberOrNull(d.amount);

      if (
        !isNil(d.amount) &&
        String(d.amount).trim() !== "" &&
        amt === null
      ) {
        return "Amount must be a number.";
      }

      if (amt !== null && amt < 0) {
        return "Amount cannot be negative.";
      }

      const owner = isNil(d.owner_resource_id)
        ? null
        : toInt(d.owner_resource_id);

      if (owner !== null) {
        const ok = (salesOwners || []).some(
          (o) => toInt(o.id) === owner
        );

        if (!ok) {
          return "Owner must be an active sales-enabled resource (is_sales=true).";
        }
      }

      return "";
    },
    [salesOwners, STAGE_OPTIONS]
  );

  const handleSave = useCallback(async () => {
    if (!canEdit || !onSave || !draft) return;

    setSaveErr("");

    const verr = validateDraft(draft);
    setValidationErr(verr);

    if (verr) return;

    try {
      // ensure base snapshot uses canonical stage too
      const patch = buildPatch(draft);
      await onSave(patch);

      // keep local draft canonical post-save
      setDraft((d) => {
        if (!d) return d;
        return {
          ...d,
          stage: canonicalStage(d.stage),
        };
      });

      setBaseSnapshot(
        stableStringify(
          shallowPickComparable({
            ...draft,
            stage: canonicalStage(draft.stage),
          })
        )
      );
    } catch (e) {
      setSaveErr(
        e?.response?.data?.detail ||
          String(e?.message || "Save failed")
      );
    }
  }, [canEdit, onSave, draft, buildPatch, validateDraft]);

  // ---------- Early returns AFTER all hooks ----------
  if (loading) {
    return (
      <div
        className="h-auto min-h-0 w-full overflow-visible md:h-full md:overflow-hidden"
        style={wrap}
      >
        <div style={{ fontSize: 12, opacity: 0.75 }}>Loading...</div>
        <Tooltip />
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="h-auto min-h-0 w-full overflow-visible md:h-full md:overflow-hidden"
        style={wrap}
      >
        <div
          className="break-words"
          style={{
            color: theme?.danger || "#b91c1c",
            fontSize: 12,
            overflowWrap: "anywhere",
          }}
        >
          {error}
        </div>

        <div className="mt-2.5">
          <button
            type="button"
            className="min-h-[44px] w-full bg-btnGeneralNormal text-white hover:bg-btnGeneralHover active:bg-btnGeneralClick sm:w-auto"
            style={{
              borderRadius: 10,
              padding: "10px 14px",
              fontWeight: 900,
            }}
            onClick={() => onReload && onReload()}
          >
            Retry
          </button>
        </div>

        <Tooltip />
      </div>
    );
  }

  if (!draft) {
    return (
      <div
        className="h-auto min-h-0 w-full overflow-visible md:h-full md:overflow-hidden"
        style={wrap}
      >
        <div style={{ fontSize: 12, opacity: 0.75 }}>
          Select an opportunity.
        </div>
        <Tooltip />
      </div>
    );
  }

  const saveDisabled = !canEdit || !isDirty;

  return (
    <div
      className="h-auto min-h-0 w-full overflow-visible md:h-full md:overflow-hidden"
      style={wrap}
    >
      <div className="flex w-full flex-col gap-2 sm:flex-row sm:items-center sm:justify-end sm:gap-2.5">
        <button
          type="button"
          className="min-h-[44px] w-full bg-btnGeneralNormal text-white hover:bg-btnGeneralHover active:bg-btnGeneralClick sm:w-auto"
          style={{
            borderRadius: 10,
            padding: "8px 10px",
            fontWeight: 900,
            fontSize: 12,
          }}
          onClick={() => onOpenAccess && onOpenAccess()}
          disabled={!draft?.id}
        >
          Access
        </button>

        <button
          type="button"
          className="min-h-[44px] w-full bg-btnActionNormal text-white hover:bg-btnActionHover active:bg-btnActionClick sm:w-auto"
          style={{
            borderRadius: 10,
            padding: "8px 10px",
            fontWeight: 900,
            fontSize: 12,
            opacity: saveDisabled ? 0.5 : 1,
          }}
          onClick={handleSave}
          disabled={saveDisabled}
        >
          Save
        </button>
      </div>

      {validationErr ? (
        <div
          className="mt-2.5 break-words"
          style={{
            fontSize: 12,
            color: theme?.danger || "#b91c1c",
            overflowWrap: "anywhere",
          }}
        >
          {validationErr}
        </div>
      ) : null}

      {saveErr ? (
        <div
          className="mt-2.5 break-words"
          style={{
            fontSize: 12,
            color: theme?.danger || "#b91c1c",
            overflowWrap: "anywhere",
          }}
        >
          {saveErr}
        </div>
      ) : null}

      <div className="mt-2.5 grid grid-cols-1 gap-3 md:grid-cols-2">
        <div className="min-w-0">
          <LabelWithTip
            label="Customer"
            tip={
              "Resolved from opportunity.customer_id.\nRead-only here (change customer via creation flow)."
            }
            bind={bind}
            style={labelStyle}
          />

          <input
            style={{
              ...input,
              background: "#f9fafb",
            }}
            value={
              customerName
                ? customerName
                : customerId
                  ? `Customer #${customerId}`
                  : ""
            }
            disabled
          />

          <div style={hint}>
            ID: {customerId ? String(customerId) : "—"}
          </div>
        </div>

        <div className="min-w-0">
          <LabelWithTip
            label="Owner"
            tip={
              "Assign to an active sales-enabled resource.\nOnly active=true AND is_sales=true appear."
            }
            bind={bind}
            style={labelStyle}
          />

          <select
            style={{
              ...input,
              paddingRight: 30,
            }}
            value={
              isNil(draft.owner_resource_id)
                ? ""
                : String(draft.owner_resource_id)
            }
            disabled={!canEdit || salesOwnersLoading}
            onChange={(e) => {
              const v = e.target.value;
              setDraft((d) => ({
                ...d,
                owner_resource_id: v ? Number(v) : null,
              }));
            }}
          >
            <option value="">
              {salesOwnersLoading
                ? "Loading..."
                : salesOwnersErr
                  ? "Load failed"
                  : "— Select —"}
            </option>

            {(salesOwners || []).map((o) => (
              <option key={String(o.id)} value={String(o.id)}>
                {o.label}
              </option>
            ))}
          </select>

          {salesOwnersErr ? (
            <div
              className="mt-1.5 break-words"
              style={{
                fontSize: 12,
                color: theme?.danger || "#b91c1c",
                overflowWrap: "anywhere",
              }}
            >
              {salesOwnersErr}
            </div>
          ) : null}

          <div style={hint}>
            Owner Resource ID:{" "}
            {ownerResourceId ? String(ownerResourceId) : "—"} · Owner User ID:{" "}
            {ownerUserId ? String(ownerUserId) : "—"}
          </div>
        </div>
      </div>

      <div style={sectionTitle}>Core</div>

      <div className="mt-2.5 grid grid-cols-1 gap-3 md:grid-cols-2">
        <div className="min-w-0">
          <LabelWithTip
            label="Name"
            tip={"Client-facing title.\nKeep it short and specific."}
            bind={bind}
            style={labelStyle}
          />

          <input
            style={input}
            value={draft.name || ""}
            disabled={!canEdit}
            onChange={(e) =>
              setDraft((d) => ({
                ...d,
                name: e.target.value,
              }))
            }
          />
        </div>

        <div className="min-w-0">
          <LabelWithTip
            label="Expected Close Date"
            tip={"Used for forecasting.\nKeep realistic."}
            bind={bind}
            style={labelStyle}
          />

          <input
            type="date"
            style={input}
            value={normDateForInput(draft.expected_close_date)}
            disabled={!canEdit}
            onChange={(e) =>
              setDraft((d) => ({
                ...d,
                expected_close_date: e.target.value || null,
              }))
            }
          />
        </div>
      </div>

      <div style={sectionTitle}>Pipeline</div>

      <div className="mt-2.5 grid grid-cols-1 gap-3 md:grid-cols-2">
        <div className="min-w-0">
          <LabelWithTip
            label="Stage"
            tip={"Pipeline stage.\nUsed for reporting."}
            bind={bind}
            style={labelStyle}
          />

          <select
            style={{
              ...input,
              paddingRight: 30,
            }}
            value={stageValue}
            disabled={!canEdit}
            onChange={(e) => {
              const v = canonicalStage(e.target.value);
              setDraft((d) => ({
                ...d,
                stage: v,
              }));
            }}
          >
            <option value="">— Select —</option>

            {stageOptions.map((s) => (
              <option key={String(s)} value={String(s)}>
                {stageLabel(s)}
              </option>
            ))}
          </select>
        </div>

        <div className="min-w-0">
          <LabelWithTip
            label="Status"
            tip={"Outcome state.\nWon/Lost drive timestamps."}
            bind={bind}
            style={labelStyle}
          />

          <select
            style={{
              ...input,
              paddingRight: 30,
            }}
            value={statusValue}
            disabled={!canEdit}
            onChange={(e) =>
              setDraft((d) => ({
                ...d,
                status: e.target.value,
              }))
            }
          >
            <option value="">— Select —</option>

            {statusOptions.map((s) => (
              <option key={String(s)} value={String(s)}>
                {String(s)}
              </option>
            ))}
          </select>
        </div>

        <div className="min-w-0">
          <LabelWithTip
            label="Probability (%)"
            tip={"0–100.\nUsed for weighted pipeline totals."}
            bind={bind}
            style={labelStyle}
          />

          <input
            style={input}
            value={
              isNil(draft.probability)
                ? ""
                : String(draft.probability)
            }
            disabled={!canEdit}
            onChange={(e) =>
              setDraft((d) => ({
                ...d,
                probability: e.target.value,
              }))
            }
            inputMode="decimal"
            placeholder="0–100"
          />
        </div>

        <div className="min-w-0">
          <LabelWithTip
            label="Amount"
            tip={"Numeric.\nUsed for pipeline totals."}
            bind={bind}
            style={labelStyle}
          />

          <input
            style={input}
            value={isNil(draft.amount) ? "" : String(draft.amount)}
            disabled={!canEdit}
            onChange={(e) =>
              setDraft((d) => ({
                ...d,
                amount: e.target.value,
              }))
            }
            inputMode="decimal"
            placeholder="e.g. 1250"
          />
        </div>
      </div>

      <div style={sectionTitle}>People</div>

      <div className="mt-2.5 grid grid-cols-1 gap-3 md:grid-cols-2">
        <div className="min-w-0">
          <LabelWithTip
            label="Primary Contact"
            tip={"Main point of contact.\nFiltered by customer and sorted A→Z."}
            bind={bind}
            style={labelStyle}
          />

          <select
            style={{
              ...input,
              paddingRight: 30,
            }}
            value={
              isNil(draft.primary_contact_id)
                ? ""
                : String(draft.primary_contact_id)
            }
            disabled={!canEdit || !customerId || contactsLoading}
            onChange={(e) => {
              const v = e.target.value;
              setDraft((d) => ({
                ...d,
                primary_contact_id: v ? Number(v) : null,
              }));
            }}
          >
            <option value="">
              {contactsLoading
                ? "Loading..."
                : contactsErr
                  ? "Load failed"
                  : "— Select —"}
            </option>

            {contactOptions.map((o) => (
              <option key={String(o.id)} value={String(o.id)}>
                {o.label}
              </option>
            ))}
          </select>

          {contactsErr ? (
            <div
              className="mt-1.5 break-words"
              style={{
                fontSize: 12,
                color: theme?.danger || "#b91c1c",
                overflowWrap: "anywhere",
              }}
            >
              {contactsErr}
            </div>
          ) : null}
        </div>

        <div className="min-w-0">
          <LabelWithTip
            label="Raised By"
            tip={"Who raised the enquiry."}
            bind={bind}
            style={labelStyle}
          />

          <select
            style={{
              ...input,
              paddingRight: 30,
            }}
            value={
              isNil(draft.raised_by_contact_id)
                ? ""
                : String(draft.raised_by_contact_id)
            }
            disabled={!canEdit || !customerId || contactsLoading}
            onChange={(e) => {
              const v = e.target.value;
              setDraft((d) => ({
                ...d,
                raised_by_contact_id: v ? Number(v) : null,
              }));
            }}
          >
            <option value="">
              {contactsLoading
                ? "Loading..."
                : contactsErr
                  ? "Load failed"
                  : "— Select —"}
            </option>

            {contactOptions.map((o) => (
              <option key={String(o.id)} value={String(o.id)}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div style={sectionTitle}>Notes</div>

      <div className="mt-2.5 min-w-0">
        <LabelWithTip
          label="Notes"
          tip={
            "Requirements, objections, next steps.\nThis is where the value is."
          }
          bind={bind}
          style={labelStyle}
        />

        <textarea
          style={{
            ...input,
            minHeight: 160,
            resize: "vertical",
            marginTop: 8,
          }}
          value={draft.notes || ""}
          disabled={!canEdit}
          onChange={(e) =>
            setDraft((d) => ({
              ...d,
              notes: e.target.value,
            }))
          }
          placeholder="Add context, requirements, objections, next steps…"
        />
      </div>

      <div style={sectionTitle}>Audit</div>

      <div className="mt-2.5 grid grid-cols-1 gap-3 md:grid-cols-2">
        <div className="min-w-0">
          <div style={labelStyle}>Opportunity ID</div>
          <input
            style={{
              ...input,
              background: "#f9fafb",
            }}
            value={draft?.id ? String(draft.id) : ""}
            disabled
          />
        </div>

        <div className="min-w-0">
          <div style={labelStyle}>Tenant ID</div>
          <input
            style={{
              ...input,
              background: "#f9fafb",
            }}
            value={draft?.tenant_id ? String(draft.tenant_id) : ""}
            disabled
          />
        </div>

        <div className="min-w-0">
          <div style={labelStyle}>Created</div>
          <input
            style={{
              ...input,
              background: "#f9fafb",
            }}
            value={fmtIsoLike(draft?.created_at)}
            disabled
          />
        </div>

        <div className="min-w-0">
          <div style={labelStyle}>Updated</div>
          <input
            style={{
              ...input,
              background: "#f9fafb",
            }}
            value={fmtIsoLike(draft?.updated_at)}
            disabled
          />
        </div>

        <div className="min-w-0">
          <div style={labelStyle}>Won At</div>
          <input
            style={{
              ...input,
              background: "#f9fafb",
            }}
            value={fmtIsoLike(draft?.won_at)}
            disabled
          />
        </div>

        <div className="min-w-0">
          <div style={labelStyle}>Lost At</div>
          <input
            style={{
              ...input,
              background: "#f9fafb",
            }}
            value={fmtIsoLike(draft?.lost_at)}
            disabled
          />
        </div>
      </div>

      <div
        className="mt-3.5 break-words"
        style={{
          fontSize: 12,
          opacity: 0.75,
          overflowWrap: "anywhere",
        }}
      >
        {accessLoading ? "Loading access…" : ""}

        {accessError ? (
          <span
            style={{
              color: theme?.danger || "#b91c1c",
            }}
          >
            {accessError}
          </span>
        ) : null}

        <div className="mt-1.5">
          Actor resource:{" "}
          {actorResourceId ? String(actorResourceId) : "Unresolved"} · Edit:{" "}
          {canEdit ? "Yes" : "No"} · Dirty: {isDirty ? "Yes" : "No"}
        </div>

        <div className="mt-1.5">
          Active access rows:{" "}
          {Array.isArray(accessActiveRows) ? accessActiveRows.length : 0}
        </div>
      </div>

      <Tooltip />
    </div>
  );
}